/**
 * Pool di asset: elenchi di ambiti con un nome.
 *
 * ## Perché esistono
 *
 * Gli ambiti di una discovery si riscrivevano a mano ogni volta. Sedici subnet
 * incollate in sei posti fra job e pianificazioni divergono al primo cambio di
 * rete: qualcuno aggiorna la scansione notturna e non quella settimanale, e da
 * quel momento due inventari dello stesso perimetro danno numeri diversi senza
 * che nessuno dei due sembri sbagliato.
 *
 * ## Cosa contengono
 *
 * Ambiti — dove cercare — non righe di asset. Il nome viene dal linguaggio di
 * chi li usa; il contenuto è quello, e la scheda lo dice invece di lasciarlo
 * scoprire.
 */
import { useState } from 'react'
import type { ChangeEvent } from 'react'

import { api, ApiError } from '../api/client'
import type { AssetPool, ConditionGroup, PoolPreview, RejectedTarget } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner, timeAgo } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { colonnaTag, useTagDi } from '../components/Tag'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

export default function Pools() {
  const pools = useAsync(() => api.pools(), [])
  const [aperto, setAperto] = useState<string | null>(null)
  const [creando, setCreando] = useState(false)
  const tag = useTagDi('pool')
  const righe = pools.data ?? []
  const colonne: Column<AssetPool>[] = [
    { key: 'name', header: t('Nome') },
    // I tag vengono dal catalogo condiviso: si filtrano e si modificano sul posto.
    ...colonnaTag<AssetPool>('pool', tag, (p) => p.id),
    // Statico o dinamico: sono due cose diverse — «dove cercare» e «quali cose
    // note riguardare» — e distinguerle in elenco evita di aprirne tre per
    // trovare quello giusto.
    {
      key: 'kind',
      header: t('Tipo'),
      value: (p) => (p.kind === 'dynamic' ? t('Dinamico') : t('Statico')),
      render: (p) => (
        <span className={`badge badge--${p.kind === 'dynamic' ? 'info' : 'muted'}`}>
          {p.kind === 'dynamic' ? t('Dinamico') : t('Statico')}
        </span>
      ),
    },
    {
      key: 'description',
      header: t('Descrizione'),
      render: (p) => <span className="text-muted">{p.description ?? '—'}</span>,
    },
    {
      key: 'entries_count',
      header: t('Ambiti'),
      value: (p) => p.entries_count,
      render: (p) => (
        <>
          {p.entries_count}
          {p.kind === 'dynamic' && p.without_address ? (
            <span
              className="chip"
              title={t('{n} corrispondono alle condizioni ma non hanno un indirizzo di gestione: non si possono scansionare.', { n: p.without_address })}
            >
              {t('+{n} senza indirizzo', { n: p.without_address })}
            </span>
          ) : null}
        </>
      ),
    },
    // Il numero di indirizzi accanto a quello degli ambiti: «14 ambiti» non
    // dice se sono quaranta macchine o quarantamila, e la differenza è fra una
    // scansione di due minuti e una che non parte.
    {
      key: 'addresses',
      header: t('Indirizzi'),
      value: (p) => p.addresses,
      render: (p) => (
        <>
          {p.addresses.toLocaleString(localeCorrente())}
          {p.job_limits?.network != null && p.addresses > p.job_limits.network && (
            <span
              className="badge badge--muted"
              title={t('Una scansione di rete guarda al massimo {max} indirizzi per job: con questo pool si divide da sé in {k} job, uno per parte.', { max: p.job_limits.network, k: Math.ceil(p.addresses / p.job_limits.network) })}
            >
              {t('{k} job', { k: Math.ceil(p.addresses / p.job_limits.network) })}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'updated_at',
      header: t('Aggiornato'),
      type: 'date',
      value: (p) => p.updated_at,
      render: (p) => <span title={p.updated_at ?? ''}>{timeAgo(p.updated_at)}</span>,
    },
    {
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      render: (p) => (
        <button type="button" className="link" onClick={() => setAperto(aperto === p.id ? null : p.id)}>
          {aperto === p.id ? t('Chiudi') : t('Apri')}
        </button>
      ),
    },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Pool di asset')}</h1>
          <p className="page__subtitle">
            {t('Definisce il perimetro di discovery raggruppando subnet, sedi e ambienti. Ogni scansione utilizza il pool associato per determinare segmenti e target da acquisire; le modifiche al pool si applicano automaticamente alle pianificazioni collegate.')}
          </p>
        </div>
        <button onClick={() => setCreando(true)} disabled={creando}>
          {t('Nuovo pool')}
        </button>
      </header>

      <ErrorBanner error={pools.error} />

      {creando && (
        <NuovoPool
          onCancel={() => setCreando(false)}
          onDone={(id) => {
            setCreando(false)
            setAperto(id)
            pools.reload()
          }}
        />
      )}

      <Card>
        {pools.loading && !pools.data ? (
          <Spinner />
        ) : !pools.data?.length ? (
          <EmptyState
            title={t('Nessun pool')}
            hint={t('Un pool raccoglie le subnet di una sede o di un ambiente: si scrive una volta e lo richiamano tutte le scansioni.')}
          />
        ) : (
          <DataTable rows={righe} columns={colonne} rowKey={(p) => p.id} conteggio={{ nome: t('pool') }} />
        )}
      </Card>

      {aperto && (
        <DettaglioPool
          poolId={aperto}
          onChanged={() => pools.reload()}
          onDeleted={() => {
            setAperto(null)
            pools.reload()
          }}
        />
      )}
    </>
  )
}

function NuovoPool({
  onCancel,
  onDone,
}: {
  onCancel: () => void
  onDone: (id: string) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  // Statico o dinamico: si sceglie **adesso** perché non si cambia dopo. I due
  // contenuti non convivono — un pool che fosse entrambi sarebbe vuoto al primo
  // giro e pieno al secondo — quindi cambiare tipo vorrebbe dire buttare quello
  // che c'è dentro, e un menu che butta dei dati senza dirlo non si offre.
  const [kind, setKind] = useState<'static' | 'dynamic'>('static')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function crea() {
    setBusy(true)
    setError(null)
    try {
      // Vuoto, e si riempie dopo: gli ambiti si aggiungono a mano o si
      // importano, e chiederli qui costringerebbe a scegliere il modo prima di
      // sapere che ce ne sono due. Un pool dinamico nasce invece con una
      // condizione qualunque: senza, il Control Plane lo rifiuterebbe — un
      // insieme senza condizioni non corrisponde a niente.
      const pool = await api.createPool({
        name: name.trim(),
        description: description.trim() || null,
        kind,
        match:
          kind === 'dynamic'
            ? [{ conditions: [{ attribute: 'name', operator: 'contains', value: '' }] }]
            : undefined,
      })
      onDone(pool.id)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Nuovo pool')}>
      <ErrorBanner error={error} />
      <div className="row">
        <div className="field">
          <label htmlFor="pool-name">{t('Nome')}</label>
          <input
            id="pool-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('Sede di Bergamo')}
          />
        </div>
        <div className="field" style={{ flex: 2 }}>
          <label htmlFor="pool-desc">{t('Descrizione')}</label>
          <input
            id="pool-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('A cosa corrisponde, per chi lo troverà fra sei mesi')}
          />
        </div>
      </div>
      <div className="field">
        <label>{t('Che cosa contiene')}</label>
        <div className="rowactions" style={{ textAlign: 'left', margin: '0 0 6px' }}>
          <button
            type="button"
            className={kind === 'static' ? '' : 'secondary'}
            onClick={() => setKind('static')}
          >
            {t('Reti e indirizzi che scrivo io')}
          </button>
          <button
            type="button"
            className={kind === 'dynamic' ? '' : 'secondary'}
            onClick={() => setKind('dynamic')}
          >
            {t("Condizioni sull'inventario")}
          </button>
        </div>
        <p className="field__hint">
          {kind === 'static'
            ? t('Dice dove cercare, e funziona anche la prima volta: è l’unico tipo utile quando in CMDB non c’è ancora niente.')
            : t('Dice quali macchine già note riguardare — «quelle con la 22 aperta», «quelle che si chiamano SRV-*». Non può decidere la prima scansione: una porta aperta si sa dopo averla guardata.')}
        </p>
      </div>

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={crea} disabled={busy || !name.trim()}>
          {busy ? t('Creazione…') : t('Crea')}
        </button>
        <button className="secondary" onClick={onCancel} disabled={busy}>
          {t('Annulla')}
        </button>
      </div>
    </Card>
  )
}

/** Una riga del modulo: ambito e nota, come si scrivono. */
type Riga = { value: string; note: string }

function DettaglioPool({
  poolId,
  onChanged,
  onDeleted,
}: {
  poolId: string
  onChanged: () => void
  onDeleted: () => void
}) {
  const pool = useAsync(() => api.pool(poolId), [poolId])
  const [righe, setRighe] = useState<Riga[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [rifiutate, setRifiutate] = useState<RejectedTarget[]>([])
  // Il nome è ciò con cui il pool si cita nelle pianificazioni e nei job:
  // si cambia dalla scheda, senza rifare il pool e ricopiarne gli ambiti.
  const [rinominando, setRinominando] = useState(false)
  const [nuovoNome, setNuovoNome] = useState('')
  const [nuovaDescrizione, setNuovaDescrizione] = useState('')

  // Le righe si prendono dal server una volta sola: rileggerle a ogni render
  // butterebbe via ciò che si sta scrivendo.
  const correnti: Riga[] =
    righe ??
    (pool.data?.entries ?? []).map((e) => ({ value: e.value, note: e.note ?? '' }))

  function aggiorna(prossime: Riga[]) {
    setRighe(prossime)
    setEsito(null)
  }

  async function salva() {
    setBusy(true)
    setError(null)
    setRifiutate([])
    try {
      const salvato = await api.setPoolEntries(
        poolId,
        correnti
          .filter((r) => r.value.trim())
          .map((r) => ({ value: r.value.trim(), note: r.note.trim() || null })),
      )
      setRighe(null)
      setEsito(t('Salvato: {ambiti} ambiti, {indirizzi} indirizzi.', { ambiti: salvato.entries_count, indirizzi: salvato.addresses }))
      pool.reload()
      onChanged()
    } catch (err) {
      setError(err)
      setRifiutate(estraiRifiutate(err))
    } finally {
      setBusy(false)
    }
  }

  async function importa(testo: string, skipInvalid: boolean) {
    setBusy(true)
    setError(null)
    setRifiutate([])
    try {
      const esitoImport = await api.importPool(poolId, testo, { skipInvalid })
      setRighe(null)
      // Frasi intere, una per fatto: i pezzi facoltativi non si traducono
      // incollati l'uno all'altro.
      setEsito(
        [
          t('Importati {n} ambiti.', { n: esitoImport.imported ?? 0 }),
          esitoImport.duplicates
            ? t('{n} già presenti nel file.', { n: esitoImport.duplicates })
            : '',
          esitoImport.rejected?.length
            ? t('{n} righe lasciate indietro.', { n: esitoImport.rejected.length })
            : '',
        ]
          .filter(Boolean)
          .join(' '),
      )
      setRifiutate(esitoImport.rejected ?? [])
      pool.reload()
      onChanged()
    } catch (err) {
      setError(err)
      setRifiutate(estraiRifiutate(err))
    } finally {
      setBusy(false)
    }
  }

  function apriRinomina() {
    setNuovoNome(pool.data?.name ?? '')
    setNuovaDescrizione(pool.data?.description ?? '')
    setEsito(null)
    setRinominando(true)
  }

  async function rinomina() {
    const nome = nuovoNome.trim()
    if (!nome) return
    setBusy(true)
    setError(null)
    try {
      await api.updatePool(poolId, { name: nome, description: nuovaDescrizione.trim() || null })
      setRinominando(false)
      setEsito(t('Pool rinominato in «{nome}».', { nome }))
      pool.reload()
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function elimina() {
    if (!window.confirm(t('Eliminare il pool «{nome}»?', { nome: pool.data?.name }))) return
    setBusy(true)
    setError(null)
    try {
      await api.deletePool(poolId)
      onDeleted()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  if (pool.loading && !pool.data) return <Spinner />
  if (!pool.data) return <ErrorBanner error={pool.error} />

  const indirizzi = correnti.length ? undefined : pool.data.addresses

  return (
    <Card title={t('Pool: {nome}', { nome: pool.data.name })}>
      <ErrorBanner error={error} />
      {esito && <div className="banner banner--ok">{esito}</div>}
      {pool.data.description && !rinominando && (
        <p className="text-muted" style={{ marginTop: 0 }}>{pool.data.description}</p>
      )}

      {rinominando && (
        <form
          className="form-grid"
          style={{ marginBottom: 16 }}
          onSubmit={(e) => {
            e.preventDefault()
            void rinomina()
          }}
        >
          <div className="field">
            <label htmlFor="pool-rename-name">{t('Nome')}</label>
            <input
              id="pool-rename-name"
              value={nuovoNome}
              onChange={(e) => setNuovoNome(e.target.value)}
              maxLength={120}
              required
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="pool-rename-description">{t('Descrizione')}</label>
            <input
              id="pool-rename-description"
              value={nuovaDescrizione}
              onChange={(e) => setNuovaDescrizione(e.target.value)}
              maxLength={500}
            />
          </div>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button type="submit" disabled={busy || !nuovoNome.trim()}>
              {busy ? t('Salvataggio…') : t('Salva il nome')}
            </button>
            <button type="button" className="secondary" onClick={() => setRinominando(false)} disabled={busy}>
              {t('Annulla')}
            </button>
          </div>
          <p className="field__hint">
            {t('Le pianificazioni e i job che usano questo pool lo seguono per identità: il nome nuovo compare dalla prossima esecuzione.')}
          </p>
        </form>
      )}

      {/* Le righe rifiutate con il loro numero: su duecento righe, «un ambito
          non valido» senza dire quale è un rifiuto che non aiuta a rimediare. */}
      {rifiutate.length > 0 && (
        <div className="banner banner--warn">
          <strong>{t('Righe non valide:')}</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: '1.2em' }}>
            {rifiutate.map((r) => (
              <li key={`${r.line}-${r.value}`}>
                {t('riga {n}', { n: r.line })} — <span className="mono">{r.value}</span>: {r.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {pool.data.kind === 'dynamic' ? (
        <Condizioni
          pool={pool.data}
          onSaved={() => {
            pool.reload()
            onChanged()
          }}
        />
      ) : (
        <Popola onTesto={importa} disabled={busy} />
      )}

      <div
        className="table-wrap"
        style={{ marginTop: 16, display: pool.data.kind === 'dynamic' ? 'none' : undefined }}
      >
        <table>
          <thead>
            <tr>
              <th style={{ width: '35%' }}>{t('Ambito')}</th>
              <th>{t('A cosa corrisponde')}</th>
              <th style={{ width: 110 }}>{t('Indirizzi')}</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {correnti.map((riga, i) => (
              <tr key={i}>
                <td>
                  <input
                    className="mono"
                    value={riga.value}
                    placeholder="10.21.0.0/24"
                    onChange={(e) =>
                      aggiorna(
                        correnti.map((r, j) =>
                          j === i ? { ...r, value: e.target.value } : r,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  <input
                    value={riga.note}
                    placeholder={t('VLAN stampanti')}
                    onChange={(e) =>
                      aggiorna(
                        correnti.map((r, j) => (j === i ? { ...r, note: e.target.value } : r)),
                      )
                    }
                  />
                </td>
                <td className="text-muted">
                  {pool.data?.entries?.find((e) => e.value === riga.value)?.addresses ?? '—'}
                </td>
                <td>
                  <button
                    type="button"
                    className="link"
                    onClick={() => aggiorna(correnti.filter((_, j) => j !== i))}
                  >
                    {t('Togli')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rowactions" style={{ textAlign: 'left', marginTop: 12 }}>
        {pool.data.kind === 'static' && (
          <>
            <button
              className="secondary"
              onClick={() => aggiorna([...correnti, { value: '', note: '' }])}
              disabled={busy}
            >
              {t('Aggiungi ambito')}
            </button>
            <button onClick={salva} disabled={busy}>
              {busy ? t('Salvataggio…') : t('Salva gli ambiti')}
            </button>
          </>
        )}
        <button className="secondary" onClick={apriRinomina} disabled={busy || rinominando}>
          {t('Rinomina')}
        </button>
        <button className="danger" onClick={elimina} disabled={busy}>
          {t('Elimina il pool')}
        </button>
      </div>

      {pool.data.kind === 'static' && (
        <p className="field__hint" style={{ marginTop: 12 }}>
          {t('Formati: subnet {subnet} (fino a /16), indirizzo singolo {indirizzo}, intervallo {intervallo}. Solo IPv4, come la scansione di rete.', {
            subnet: '10.21.0.0/24',
            indirizzo: '10.21.0.5',
            intervallo: '10.21.0.10-10.21.0.40',
          })}
          {indirizzi !== undefined && ` ${t('In tutto {n} indirizzi.', { n: indirizzi })}`}
        </p>
      )}
    </Card>
  )
}

/**
 * Il costruttore delle condizioni di un pool dinamico.
 *
 * ## La forma è la logica
 *
 * Blocchi separati da «oppure», condizioni dentro un blocco separate da «e».
 * Non parentesi: così non c'è precedenza da dichiarare e quindi non c'è
 * precedenza da sbagliare. «A e B oppure C» letto in due modi dà due perimetri
 * diversi, **entrambi plausibili** — quindi nessuno si accorge di aver scritto
 * l'altro, e ci si accorge dell'errore quando manca qualcosa che non si sa che
 * manchi.
 *
 * ## Perché l'anteprima non è un di più
 *
 * Perché una condizione si scrive a occhio e si sbaglia a occhio. «Il nome
 * contiene SRV» sembra prendere i server e prende anche `PRINT-SRV-2`;
 * «l'elenco contiene 22» con l'operatore sbagliato prende anche 1022 e 2222. In
 * tutti questi casi la scansione **riesce**: guarda un perimetro diverso da
 * quello inteso e restituisce un inventario plausibile. Vedere il numero prima
 * di salvare costa una richiesta e toglie l'intera classe di errore.
 */
function Condizioni({ pool, onSaved }: { pool: AssetPool; onSaved: () => void }) {
  const [blocchi, setBlocchi] = useState<ConditionGroup[]>(
    pool.match?.length ? pool.match : [{ conditions: [] }],
  )
  const [anteprima, setAnteprima] = useState<PoolPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)

  // Operatori e campi del CI vengono dal server: la console non li inventa, o
  // il giorno che ne nasce uno nuovo ci sarebbe un elenco da aggiornare a mano
  // in due posti.
  const modello = useAsync(() => api.editableTaxonomy(), [])
  // I nomi degli attributi **osservati davvero** su questo tenant: scrivere a
  // memoria «os_family» o «osFamily» è il modo più diretto di ottenere una
  // condizione che non corrisponde a niente e sembra giusta.
  const osservati = useAsync(() => api.observedSchema(), [])

  function cambia(prossimi: ConditionGroup[]) {
    setBlocchi(prossimi)
    // L'anteprima è di **quelle** condizioni: lasciarla lì mentre si modifica
    // farebbe leggere un numero che non corrisponde più a quello che si vede.
    setAnteprima(null)
    setEsito(null)
  }

  async function prova() {
    setBusy(true)
    setError(null)
    try {
      setAnteprima(await api.previewPool(blocchi))
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function salva() {
    setBusy(true)
    setError(null)
    try {
      const salvato = await api.updatePool(pool.id, { match: blocchi })
      setEsito(
        t('Salvato: {n} apparati corrispondono, {conIndirizzo} con un indirizzo da scansionare.', {
          n: salvato.matched ?? 0,
          conIndirizzo: salvato.entries_count,
        }),
      )
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const operatori = modello.data?.operators ?? []
  const senzaValore = ['exists', 'missing']

  return (
    <>
      <ErrorBanner error={error} />
      {esito && <div className="banner banner--ok">{esito}</div>}

      <datalist id="pool-attributi">
        {(modello.data?.ci_fields ?? []).map((f) => (
          <option key={f} value={f} />
        ))}
        {(osservati.data ?? []).map((a) => (
          <option key={a.attribute} value={a.attribute} />
        ))}
      </datalist>

      {blocchi.map((blocco, i) => (
        <div key={i} className="kindform" style={{ marginBottom: 12 }}>
          <h4 className="kindform__title">
            {i === 0 ? t('Corrisponde se…') : t('…oppure se…')}
          </h4>

          {blocco.conditions.length === 0 && (
            <p className="field__hint">
              {t('Un blocco senza condizioni corrisponderebbe a ogni apparato: aggiungine almeno una, o togli il blocco.')}
            </p>
          )}

          {blocco.conditions.map((c, j) => (
            <div className="row" key={j} style={{ alignItems: 'flex-end' }}>
              <div className="field" style={{ flex: '1 1 180px' }}>
                {j === 0 ? <label>{t('Campo')}</label> : <label>{t('e il campo')}</label>}
                <input
                  list="pool-attributi"
                  className="mono"
                  value={c.attribute}
                  placeholder={t('name, open_tcp_ports, os_family…')}
                  onChange={(e) =>
                    cambia(
                      blocchi.map((b, bi) =>
                        bi !== i
                          ? b
                          : {
                              conditions: b.conditions.map((cc, ci) =>
                                ci === j ? { ...cc, attribute: e.target.value } : cc,
                              ),
                            },
                      ),
                    )
                  }
                />
              </div>
              <div className="field" style={{ flex: '1 1 160px' }}>
                <label>{t('Operatore')}</label>
                <select
                  value={c.operator}
                  onChange={(e) =>
                    cambia(
                      blocchi.map((b, bi) =>
                        bi !== i
                          ? b
                          : {
                              conditions: b.conditions.map((cc, ci) =>
                                ci === j ? { ...cc, operator: e.target.value } : cc,
                              ),
                            },
                      ),
                    )
                  }
                >
                  {operatori.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: '1 1 160px' }}>
                <label>{t('Valore')}</label>
                <input
                  value={c.value}
                  disabled={senzaValore.includes(c.operator)}
                  placeholder={senzaValore.includes(c.operator) ? t('non serve') : ''}
                  onChange={(e) =>
                    cambia(
                      blocchi.map((b, bi) =>
                        bi !== i
                          ? b
                          : {
                              conditions: b.conditions.map((cc, ci) =>
                                ci === j ? { ...cc, value: e.target.value } : cc,
                              ),
                            },
                      ),
                    )
                  }
                />
              </div>
              <div className="field" style={{ flex: '0 0 auto' }}>
                <button
                  type="button"
                  className="link"
                  onClick={() =>
                    cambia(
                      blocchi.map((b, bi) =>
                        bi !== i
                          ? b
                          : { conditions: b.conditions.filter((_, ci) => ci !== j) },
                      ),
                    )
                  }
                >
                  {t('Togli')}
                </button>
              </div>
            </div>
          ))}

          <div className="rowactions" style={{ textAlign: 'left', marginTop: 8 }}>
            <button
              type="button"
              className="secondary"
              onClick={() =>
                cambia(
                  blocchi.map((b, bi) =>
                    bi !== i
                      ? b
                      : {
                          conditions: [
                            ...b.conditions,
                            { attribute: '', operator: 'contains', value: '' },
                          ],
                        },
                  ),
                )
              }
            >
              {t('Aggiungi una condizione (e)')}
            </button>
            {blocchi.length > 1 && (
              <button
                type="button"
                className="link"
                onClick={() => cambia(blocchi.filter((_, bi) => bi !== i))}
              >
                {t('Togli il blocco')}
              </button>
            )}
          </div>
        </div>
      ))}

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button
          type="button"
          className="secondary"
          onClick={() => cambia([...blocchi, { conditions: [] }])}
        >
          {t('Aggiungi un blocco (oppure)')}
        </button>
        <button type="button" onClick={prova} disabled={busy}>
          {busy ? t('Verifica…') : t('Prova le condizioni')}
        </button>
        <button type="button" onClick={salva} disabled={busy}>
          {t('Salva le condizioni')}
        </button>
      </div>

      {anteprima && (
        <div className="banner banner--info" style={{ marginTop: 12 }}>
          <strong>
            {t('{n} apparati corrispondono, {conIndirizzo} con un indirizzo da scansionare.', {
              n: anteprima.matched,
              conIndirizzo: anteprima.targets_total,
            })}
          </strong>
          {/* I due numeri non coincidono, e la differenza va detta: un CI visto
              da vCenter o dal cloud può non avere un indirizzo di gestione — e
              senza questa riga resta la domanda «perché ne scansiona 31 e me
              ne dice 47». */}
          {anteprima.without_address > 0 && (
            <p style={{ margin: '6px 0 0' }}>
              {anteprima.without_address_examples.length > 0
                ? t('{n} corrispondono ma non hanno un indirizzo di gestione, quindi non si possono scansionare: {esempi}…', {
                    n: anteprima.without_address,
                    esempi: anteprima.without_address_examples.join(', '),
                  })
                : t('{n} corrispondono ma non hanno un indirizzo di gestione, quindi non si possono scansionare', {
                    n: anteprima.without_address,
                  })}
            </p>
          )}
          {anteprima.targets.length > 0 && (
            <p className="mono" style={{ margin: '6px 0 0', wordBreak: 'break-all' }}>
              {anteprima.targets.join(', ')}
              {anteprima.targets_total > anteprima.targets.length && ' …'}
            </p>
          )}
          {anteprima.targets_total === 0 && (
            <p style={{ margin: '6px 0 0' }}>
              {t("Così com'è, una scansione che usa questo pool non avrebbe niente da guardare — e riuscirebbe senza fare niente.")}
            </p>
          )}
        </div>
      )}
    </>
  )
}

/**
 * I due modi di riempire un pool: incollare un elenco, o caricare un file.
 *
 * ## Perché due
 *
 * Perché sono due gesti diversi. Le prime quattro subnet si incollano da una
 * chat o da un documento; le duecento righe dell'inventario di rete arrivano
 * come file. Offrire solo il file costringerebbe a salvarne uno per aggiungere
 * tre righe — e riga per riga, con un pulsante «aggiungi», venti subnet sono
 * venti clic.
 *
 * Passano dalla **stessa** lettura, con le stesse regole: quello che il
 * separatore sa fare su un file lo sa fare su un incollato, e due percorsi di
 * lettura diversi divergerebbero al primo formato nuovo.
 *
 * ## Perché la casella «importa comunque»
 *
 * La differenza fra «rifiuta tutto» e «importa il resto» non è la severità: è
 * **chi ha deciso**. Il primo è il predefinito perché un elenco importato a
 * metà produce un pool che sembra completo, e la scansione che ne nasce
 * restituisce un inventario plausibile e incompleto.
 */
function Popola({
  onTesto,
  disabled,
}: {
  onTesto: (testo: string, skipInvalid: boolean) => void
  disabled: boolean
}) {
  const [skipInvalid, setSkipInvalid] = useState(false)
  const [incollato, setIncollato] = useState('')

  async function scegli(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Azzerato, o riselezionare lo stesso file dopo averlo corretto non
    // scatenerebbe l'evento e sembrerebbe che il pulsante non funzioni.
    e.target.value = ''
    if (file) onTesto(await file.text(), skipInvalid)
  }

  return (
    <>
      <div className="field">
        <label htmlFor="pool-incolla">{t('Incolla un elenco')}</label>
        <textarea
          id="pool-incolla"
          className="mono"
          rows={4}
          value={incollato}
          disabled={disabled}
          onChange={(e) => setIncollato(e.target.value)}
          placeholder={'10.21.0.0/24;VLAN server\n10.21.1.0/24;VLAN client\n10.21.2.5'}
        />
        <p className="field__hint">
          {t('Un ambito per riga. Dopo il separatore — «;», tabulazione o «,» — la nota, se la vuoi.')}
        </p>
      </div>

      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: '0 0 auto' }}>
          <button
            type="button"
            disabled={disabled || !incollato.trim()}
            onClick={() => {
              onTesto(incollato, skipInvalid)
              setIncollato('')
            }}
          >
            {t('Aggiungi al pool')}
          </button>
        </div>
        <div className="field">
          <label htmlFor="pool-csv">{t('…oppure carica un CSV')}</label>
          <input
            id="pool-csv"
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            onChange={scegli}
            disabled={disabled}
          />
        </div>
        <div className="field" style={{ flex: '0 0 auto' }}>
          <label>
            <input
              type="checkbox"
              checked={skipInvalid}
              onChange={(e) => setSkipInvalid(e.target.checked)}
            />{' '}
            {t('Importa comunque le righe valide')}
          </label>
          <p className="field__hint">
            {t('Senza, una sola riga sbagliata annulla tutto: un pool a metà sembra completo.')}
          </p>
        </div>
      </div>
    </>
  )
}

/**
 * Le righe rifiutate che il Control Plane allega al 422.
 *
 * Il messaggio dice «tre ambiti non validi»; queste dicono **quali**, con il
 * numero di riga. Su un file di duecento righe è la differenza fra un rifiuto e
 * un rifiuto a cui si può rimediare.
 */
function estraiRifiutate(err: unknown): RejectedTarget[] {
  if (!(err instanceof ApiError)) return []
  const dettaglio = err.detail as { rejected?: RejectedTarget[] } | undefined
  return dettaglio?.rejected ?? []
}
